<template>
  <div class="page-wrap">

    <!-- ── Premium Table ─────────────────────── -->
    <PremiumTable title="Grant Member Access" subtitle="Manage access control and module permissions for society members"
      add-label="Add" :show-edit="true" :show-delete="true" @add="openAddDialog" @edit="openEditDialog"
      @delete="confirmDeleteDialog($event.id)" :columns="columns" :items="permissions" :loading="loading"
      :total-items="permissions.length" :page.sync="page" :items-per-page.sync="itemsPerPage" :searchable="true"
      :search="search" @search="onSearch">
      <!-- Custom Cells -->
      <template #col-profile_url="{ item }">
        <div class="profile-cell">
          <div class="p-avatar-wrap">
            <img
              v-if="item.profile_url"
              :src="API_BASE_URL + '/uploads/' + item.profile_url"
              class="p-avatar-img"
              style="cursor: pointer"
              @click.stop="openPhoto(API_BASE_URL + '/uploads/' + item.profile_url, item.name)"
            />
            <div v-else class="p-avatar-placeholder" :style="{ background: primaryColor + '20', color: primaryColor }">
              {{ item.name ? item.name.charAt(0).toUpperCase() : 'U' }}
            </div>
          </div>
          <div class="p-info">
            <span class="p-name">{{ item.name }}</span>
            <span class="p-id" v-if="item.mobile_no">{{ item.country_code }} {{ item.mobile_no }}</span>
          </div>
          <span class="mem-type-badge" :class="getMemberTypeClass(item.designation)">
            {{ item.designation }}
          </span>
        </div>
      </template>

      <template #col-house_number="{ item }">
        <div class="house-number-badge">
          <v-icon size="14" color="#94A3B8" v-if="item.unittype === '1'">
            mdi-home-outline
          </v-icon>
          <v-icon size="14" color="#94A3B8" v-else-if="item.unittype === '2'">
            mdi-store-outline
          </v-icon>
          <span v-if="item.block_name" class="flat-prefix">{{ item.block_name }}-</span>
          <span class="house-num">{{ item.house_number }}</span>
        </div>
      </template>

      <template #col-Permission="{ item }">
        <div class="modules-grid">
          {{ item.Permission }}
          <span v-if="!item.Permission || item.Permission.length === 0" class="dash">—</span>
        </div>
      </template>

    </PremiumTable>

    <!-- ── Add/Edit Dialog ──────────────────── -->
    <PremiumModal v-model="addPermissionDialog" :title="permission.id ? 'Edit Grant Member Access' : 'Add Grant Member Access'"
      max-width="520" @close="closeDialog" :submit-text="grantButtonText" :submit-disabled="isGrantDisabled"
      @submit="permission.id ? updatePermission(permission.id) : savePermission()">
      <div class="perm-modal-body">

        <!-- ── Mobile Dots Step Indicator (≤600px only) ── -->
        <div class="perm-dots-stepper">
          <span v-for="step in permissionSteps" :key="step.key" class="perm-dot" :class="{
            'perm-dot--done': step.completed,
            'perm-dot--active': step.active,
          }"></span>
        </div>

        <!-- ── Desktop Step Indicator (hidden on mobile) ── -->
        <div class="perm-stepper perm-stepper-desktop">
          <div v-for="(step, index) in permissionSteps" :key="step.key" class="perm-step"
            :class="{ 'perm-step--last': index === permissionSteps.length - 1 }">
            <div class="perm-step-circle" :class="{ 'is-done': step.completed, 'is-active': step.active }"
              :style="step.completed ? { borderColor: primaryColor, background: primaryColor } : step.active ? { borderColor: primaryColor, color: primaryColor } : {}">
              <v-icon v-if="step.completed" size="14" color="#fff">mdi-check</v-icon>
              <span v-else :style="step.active ? { color: primaryColor } : {}">{{ index + 1 }}</span>
            </div>
            <span class="perm-step-label" :class="{ 'is-done': step.completed, 'is-active': step.active }"
              :style="step.active ? { color: primaryColor } : {}">{{ step.label }}</span>
            <div v-if="index < permissionSteps.length - 1" class="perm-step-line"
              :class="{ 'is-done': step.completed }">
            </div>
          </div>
        </div>

        <!-- ── Selection Fields ── -->
        <div class="perm-fields-card">
          <!-- Block selector (multi-building only) -->
          <div v-if="useBlocks" class="perm-field-row">
            <PremiumSelect v-model="permission.flat_id" label="Residential block" :items="flats" item-text="name"
              item-value="id" placeholder="Select block" icon="mdi-office-building-outline"
              v-validate="!!permission.id ? '' : 'required'" name="flat" data-vv-as="Block"
              :error="errors.first('flat') || serverSideErrors.flat_id" @change="getFlatMember"
              :disabled="!!permission.id" :readonly="!!permission.id" />
          </div>

          <!-- Member selector -->
          <div class="perm-field-row" :class="{ 'no-top-border': !useBlocks }">
            <PremiumSelect v-model="permission.member_id" label="Member" :items="members" item-text="display_name"
              item-value="id" placeholder="Select member" icon="mdi-account-outline"
              v-validate="!!permission.id ? '' : 'required'" name="member" data-vv-as="Member"
              :error="errors.first('member') || serverSideErrors.member_id"
              :disabled="!!permission.id || (useBlocks && !isBlockSelected)" :readonly="!!permission.id" />
          </div>
        </div>

        <!-- ── Module Access Section ── -->
        <div class="perm-modules-card" :class="{ 'perm-modules-locked': moduleLocked }">
          <div class="perm-modules-header">
            <div class="perm-modules-title">
              <v-icon size="15" :color="moduleLocked ? '#94a3b8' : primaryColor"
                class="mr-1">mdi-shield-key-outline</v-icon>
              Module Access
              <span v-if="!moduleLocked && permission.module_id.length > 0" class="perm-count-badge"
                :style="{ background: primaryColor }">
                {{ permission.module_id.length }} / {{ modules.length }}
              </span>
            </div>
            <div class="perm-modules-actions" v-if="!moduleLocked && modules.length > 0">
              <button type="button" class="perm-action-chip"
                :class="{ 'perm-action-chip--active': permission.module_id.length === modules.length }"
                :style="permission.module_id.length === modules.length ? { background: primaryColor + '18', borderColor: primaryColor, color: primaryColor } : {}"
                @click="selectAllModules">
                <v-icon size="12" class="mr-1">mdi-check-all</v-icon>
                Select All
              </button>
              <button type="button" class="perm-action-chip perm-action-chip--clear" @click="clearAllModules"
                :disabled="permission.module_id.length === 0">
                <v-icon size="12" class="mr-1">mdi-close</v-icon>
                Clear
              </button>
            </div>
          </div>

          <!-- Lock overlay hint -->
          <div v-if="moduleLocked" class="perm-lock-hint">
            <v-icon size="18" color="#94a3b8">mdi-lock-outline</v-icon>
            <span>{{ useBlocks ? 'Select a block and member first' : 'Select a member first' }}</span>
          </div>

          <!-- Module pill grid -->
          <div v-else class="perm-pill-grid">
            <button v-for="module in modules" :key="module.id" type="button" class="perm-pill"
              :class="{ 'perm-pill--on': permission.module_id.includes(module.id) }"
              :style="permission.module_id.includes(module.id) ? { color: primaryColor, borderColor: primaryColor, background: primaryColor + '12' } : {}"
              @click="toggleModule(module.id)">
              {{ module.name }}
            </button>
          </div>

          <div class="cm2-error" v-if="moduleError || serverSideErrors.module_id">
            <v-icon size="13" color="#ef4444" class="mr-1">mdi-alert-circle-outline</v-icon>
            {{ moduleError || serverSideErrors.module_id }}
          </div>

          <!-- Info hint -->
          <div v-if="!moduleLocked" class="perm-info-hint">
            <v-icon size="14" color="#94a3b8" class="mr-1">mdi-information-outline</v-icon>
            Members can add, edit, and delete records in selected modules.
          </div>
        </div>

      </div>
    </PremiumModal>

    <!-- ── Confirm Deletion ─────────────────── -->
    <ConfirmModal v-model="deleteDialog" type="danger" title="Revoke Permission"
      message="Are you sure you want to revoke all admin permissions for this member? They will lose access to the selected modules immediately."
      confirm-text="Yes, Revoke" cancel-text="Cancel" :loading="deleteLoading" @confirm="deletePermission" />

    <!-- ── Media Viewer ────────────────────── -->
    <MediaViewer v-model="mediaViewer.open" :items="mediaViewer.items" />

  </div>
</template>

<script>
import { mapMutations, mapState, mapGetters } from "vuex";
import PremiumTable from "../../common/PremiumTable.vue";
import ConfirmModal from "../../common/ConfirmModal.vue";
import PremiumModal from "../../common/PremiumModal.vue";
import PremiumSelect from "../../common/PremiumSelect.vue";
import MediaViewer from "../../common/MediaViewer.vue";
import filterMixin from "../../../mixins/filterMixin";

export default {
  name: 'PermissionsList',
  mixins: [filterMixin],
  components: { PremiumTable, ConfirmModal, PremiumModal, PremiumSelect, MediaViewer },

  data() {
    return {
      search: "",
      permissions: [],
      members: [],
      flats: [],
      modules: [],
      loading: false,
      deleteLoading: false,
      addPermissionDialog: false,
      deleteDialog: false,
      delete_id: "",
      page: 1,
      itemsPerPage: 10,
      permission: {
        id: null,
        member_id: null,
        flat_id: "",
        module_id: [],
      },
      moduleError: "",
      serverSideErrors: {},
      user: {},
      mediaViewer: { open: false, items: [] },
    };
  },

  computed: {
    ...mapState('theme', ['isDark', 'primaryColor']),
    ...mapGetters('society', ['useBlocks', 'unitType']),
    API_BASE_URL: () => process.env.VUE_APP_API_URL,

    columns() {
      return [
        { key: 'profile_url', label: 'Member Name', width: '240px' },
        { key: 'house_number', label: 'Unit', width: '120px' },
        { key: 'Permission', label: 'Authorized Modules' },
      ];
    },

    isBlockSelected() {
      return !this.useBlocks || (!!this.permission.flat_id && this.permission.flat_id !== '' && this.permission.flat_id !== 'null');
    },

    isMemberSelected() {
      return !!this.permission.member_id;
    },

    moduleLocked() {
      return !(this.isBlockSelected && this.isMemberSelected);
    },

    isGrantDisabled() {
      return this.moduleLocked || !this.permission.module_id || this.permission.module_id.length === 0;
    },

    selectedMemberName() {
      const member = this.members.find(m => String(m.id) === String(this.permission.member_id));
      if (member && member.display_name) return member.display_name;
      return "member";
    },

    grantButtonText() {
      if (this.isGrantDisabled) return "Select at least one module to continue";
      return `Grant access to ${this.selectedMemberName}`;
    },

    permissionSteps() {
      const steps = [];
      if (this.useBlocks) {
        steps.push({ key: 'block', label: 'Block & Unit', completed: this.isBlockSelected, active: !this.isBlockSelected });
      }
      const memberActive = this.isBlockSelected && !this.isMemberSelected;
      steps.push({ key: 'member', label: 'Member', completed: this.isMemberSelected, active: memberActive });
      const modulesActive = this.isBlockSelected && this.isMemberSelected && (!this.permission.module_id || this.permission.module_id.length === 0);
      steps.push({ key: 'modules', label: 'Modules', completed: this.permission.module_id && this.permission.module_id.length > 0, active: modulesActive });
      return steps;
    },
  },

  watch: {
    search() {
      this.page = 1;
      this.getPermissions();
    },
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

        getMemberTypeClass(type) {
      if (type == 'Society Admin') return 'type-owner';
      if (type == 'Committee Member') return 'type-tenant';
      return 'type-default';
    },

    onSearch(val) {
      this.search = val;
    },

    getPermissions() {
      this.loading = true;
      let url = `societyadmin/permissions?society_id=${this.user.id}`;
      if (this.search) url += `&search=${this.search}`;

      this.$axios.get(url).then(response => {
        this.loading = false;
        const data = response.data;
        if (data.status) {
          this.permissions = data.permissions;
        } else {
          this.showError(data.message);
          if (data.type === 'invalid') {
            localStorage.removeItem("user");
            this.$router.push("/societyadmin/login");
          }
        }
      }).catch(() => { this.loading = false; });
    },

    getMembers() {
      // When no blocks: fetch all society members (same as committee member)
      this.$axios.get(`societyadmin/members?society_id=${this.user.id}&pagination=false`).then(response => {
        if (response.data.status) {
          const arr = Array.isArray(response.data.members)
            ? response.data.members
            : response.data.members.data || [];
          this.members = arr.map(m => ({
            ...m,
            id: m.member_identity_id,
            display_name: `${m.name} (${m.house_number})`,
          }));
        }
      });
    },

    getFlat() {
      return this.$axios.get("societyadmin/flats").then(response => {
        const data = response.data;
        if (data.status) {
          let flats = [...data.flats];
          if (this.unitType === 3 && this.useBlocks) {
            flats.unshift({ id: "all_shops", name: "All Shops" });
          }
          this.flats = flats;
        }
      });
    },

    getFlatMember() {
      this.members = [];
      this.permission.member_id = null;
      let url = `/societyadmin/members?society_id=${this.user.id}`;
      if (this.permission.flat_id === "all_shops") {
        url += "&unit_type=2";
      } else if (
        this.permission.flat_id &&
        this.permission.flat_id !== "null" &&
        this.permission.flat_id !== ""
      ) {
        url = `/societyadmin/flats/${this.permission.flat_id}/members`;
      }
      this.$axios.get(url).then(response => {
        const data = response.data;
        if (data.status) {
          const arr = Array.isArray(data.members)
            ? data.members
            : data.members.data || [];
          this.members = arr.map(m => {
            const prefix = this.unitType == 3
              ? m.unit_type == 2 ? "Shop " : "House "
              : "";
            return {
              ...m,
              id: m.member_identity_id, display_name: `${m.name} (${prefix}${m.house_number})`
            };
          });
        }
      });
    },

    getModules() {
      this.$axios.get("societyadmin/modules").then(response => {
        if (response.data.status) this.modules = response.data.modules;
      });
    },

    savePermission() {
      this.$validator.validateAll().then(success => {
        if (success) {
          if (!this.permission.module_id || this.permission.module_id.length === 0) {
            this.moduleError = "Please select at least one module.";
            return;
          }
          this.permission.society_id = this.user.id;
          this.$axios.post("/societyadmin/permissions", this.permission).then(response => {
            const data = response.data;
            if (data.status) {
              this.getPermissions();
              this.closeDialog();
              this.setAlert({ showAlert: true, variant: "green accent-4", timeout: "2000", message: "Permissions granted successfully" });
            } else {
              this.handleErrors(data);
            }
          });
        }
      });
    },

    updatePermission(id) {
      this.$validator.validateAll().then(success => {
        if (success) {
          if (!this.permission.module_id || this.permission.module_id.length === 0) {
            this.moduleError = "Please select at least one module.";
            return;
          }
          this.permission.society_id = this.user.id;
          this.$axios.put("/societyadmin/permissions/" + id, this.permission).then(response => {
            const data = response.data;
            if (data.status) {
              this.getPermissions();
              this.closeDialog();
              this.setAlert({ showAlert: true, variant: "green accent-4", timeout: "2000", message: "Permissions updated successfully" });
            } else {
              this.handleErrors(data);
            }
          });
        }
      });
    },

    deletePermission() {
      this.deleteLoading = true;
      this.$axios.delete("societyadmin/permissions/" + this.delete_id).then(response => {
        this.deleteLoading = false;
        const data = response.data;
        if (data.status) {
          this.deleteDialog = false;
          this.getPermissions();
          this.setAlert({ showAlert: true, variant: "green accent-4", timeout: "2000", message: data.message });
        } else {
          this.showError(data.message);
        }
      }).catch(() => { this.deleteLoading = false; });
    },

    toggleModule(moduleId) {
      if (this.moduleLocked) return;
      const index = this.permission.module_id.findIndex(id => String(id) === String(moduleId));
      if (index >= 0) {
        this.permission.module_id.splice(index, 1);
      } else {
        this.permission.module_id.push(moduleId);
      }
      this.moduleError = "";
    },

    selectAllModules() {
      if (this.moduleLocked) return;
      this.permission.module_id = this.modules.map(module => module.id);
      this.moduleError = "";
    },

    clearAllModules() {
      if (this.moduleLocked) return;
      this.permission.module_id = [];
      this.moduleError = "";
    },

    getPermissionDetail(id) {
      this.loading = true;
      this.$axios.get("societyadmin/permissionsDetail?member_id=" + id).then(response => {
        this.loading = false;
        const data = response.data;
        if (data.status) {
          this.permission.member_id = id;
          this.permission.id = id;
          this.permission.module_id = data.permissions.map(x => x.module_id);
        } else {
          this.showError(data.message);
        }
      }).catch(() => { this.loading = false; });
    },

    openAddDialog() {
      this.permission = { id: null, member_id: null, flat_id: "", module_id: [] };
      this.moduleError = "";
      this.members = [];

      if (this.useBlocks) {
        this.getFlat(); // ← re-fetch flats every time dialog opens
      } else {
        this.getMembers();
      }

      this.addPermissionDialog = true;
    },

    openEditDialog(item) {
      this.moduleError = "";
      const flatId = item.flat_id ? (isNaN(item.flat_id) ? item.flat_id : Number(item.flat_id)) : null;
      this.permission = {
        id: item.id,
        member_id: item.id,
        flat_id: flatId,
        module_id: []
      };
      // Ensure flats loaded
      if (this.useBlocks && this.flats.length === 0) {
        this.getFlat();
      }
      // Load members for this block WITHOUT resetting member_id
      if (this.useBlocks && flatId) {
        let url = `/societyadmin/flats/${flatId}/members`;
        this.$axios.get(url).then(response => {
          const data = response.data;
          if (data.status) {
            const arr = Array.isArray(data.members) ? data.members : data.members.data || [];
            this.members = arr.map(m => ({
              ...m,
              display_name: `${m.name} (${m.house_number})`,
            }));
          }
        });
      } else {
        this.getMembers();
      }
      this.getPermissionDetail(item.id);
      this.addPermissionDialog = true;
    },

    confirmDeleteDialog(id) {
      this.delete_id = id;
      this.deleteDialog = true;
    },

    closeDialog() {
      this.addPermissionDialog = false;
      this.deleteDialog = false;
      this.permission = { id: null, member_id: null, flat_id: "", module_id: [] };
      this.members = [];
      this.moduleError = "";
      this.$validator.reset();
      this.serverSideErrors = {};
    },

    handleErrors(data) {
      if (data.errors) {
        this.serverSideErrors = {};
        data.errors.forEach(e => { this.serverSideErrors[e.field] = e.message; });
      } else {
        this.showError(data.message);
      }
    },

    showError(msg) {
      this.setAlert({ showAlert: true, variant: "red accent-3", timeout: "2000", message: msg });
    },

    openPhoto(url, name) {
      this.mediaViewer.items = [{ url, type: "image", name: name || "Photo" }];
      this.mediaViewer.open = true;
    },
  },

  mounted() {
    console.log('useBlocks:', this.useBlocks);
    this.user = localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user")) : {};

    // Initialize persistent filters
    this.initFilters('permission');

    this.getPermissions();
    if (this.useBlocks) {
      this.getFlat();
    } else {
      this.getMembers();
    }
    this.getModules();
  },
};
</script>

<style scoped>
.page-wrap {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ── Cells ── */
.profile-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.p-avatar-wrap {
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  min-width: 42px;
  min-height: 42px;
  border-radius: 50%;
  overflow: hidden;
  background: var(--surface2);
  border: 1px solid var(--border);
}

.p-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.18s, opacity 0.18s;
}
.p-avatar-img:hover {
  transform: scale(1.08);
  opacity: 0.9;
}

.p-avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 800;
}

.p-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.p-name {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--tx);
}

.p-id {
  font-size: 11px;
  font-weight: 500;
  color: var(--tx3);
}

.unit-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  background: var(--surface2, #F1F5F9);
  color: var(--tx2, #475569);
  font-size: 11.5px;
  font-weight: 700;
  border: 1px solid var(--border);
}

.house-number-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px;
  border-radius: 8px;
  background: var(--surface2, #f8fafc);
  border: 1px solid var(--border, #e2e8f0);
  font-size: 12px;
  /* font-weight: 700; */
  color: var(--tx, #111827);
}

.house-number-badge .flat-prefix {
  color: var(--tx3, #6b7280);
}

.house-number-badge .house-num {
  color: var(--tx3, #6b7280);
}

.modules-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 4px 0;
}

.module-pill {
  padding: 3px 10px;
  border-radius: 6px;
  border: 1px solid transparent;
  font-size: 11px;
  font-weight: 700;
  transition: all 0.2s;
}

.module-pill:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* ── Modal Body ── */
.perm-modal-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ── Step Indicator ── */
.perm-stepper {
  display: flex;
  align-items: center;
  background: var(--surface2, #f8fafc);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 12px;
  padding: 12px 16px;
  gap: 0;
}

.perm-step {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.perm-step--last {
  flex: none;
}

.perm-step-circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid var(--border, #d1d5db);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: var(--tx3, #94a3b8);
  flex-shrink: 0;
  transition: all 0.25s ease;
  background: var(--surface, #fff);
}

.perm-step-circle.is-done {
  color: #fff;
}

.perm-step-circle.is-active {
  border-width: 2px;
}

.perm-step-label {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--tx3, #94a3b8);
  white-space: nowrap;
  transition: color 0.2s;
}

.perm-step-label.is-done {
  color: var(--tx, #111827);
}

.perm-step-label.is-active {
  font-weight: 700;
}

.perm-step-line {
  flex: 1;
  height: 1.5px;
  background: var(--border, #e5e7eb);
  margin: 0 8px;
  transition: background 0.25s;
}

.perm-step-line.is-done {
  background: var(--brand, #10b981);
}

/* ── Fields Card ── */
.perm-fields-card {
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 12px;
  overflow: hidden;
}

.perm-field-row {
  padding: 14px 16px;
  border-top: 1px solid var(--border, #e5e7eb);
}

.perm-field-row:first-child,
.perm-field-row.no-top-border {
  border-top: none;
}

/* ── Modules Card ── */
.perm-modules-card {
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 12px;
  padding: 14px 16px;
  transition: opacity 0.2s;
}

.perm-modules-card.perm-modules-locked {
  background: var(--surface2, #f8fafc);
}

.perm-modules-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  gap: 8px;
  flex-wrap: wrap;
}

.perm-modules-title {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 700;
  color: var(--tx, #111827);
  gap: 4px;
}

.perm-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10.5px;
  font-weight: 800;
  color: #fff;
  border-radius: 20px;
  padding: 1px 8px;
  margin-left: 6px;
}

.perm-modules-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.perm-action-chip {
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: 1.5px solid var(--border, #d1d5db);
  background: var(--surface2, #f8fafc);
  color: var(--tx2, #475569);
  transition: all 0.18s ease;
}

.perm-action-chip:hover:not(:disabled) {
  border-color: var(--tx3, #64748b);
  color: var(--tx, #111827);
}

.perm-action-chip--clear {
  color: #ef4444;
  border-color: #fca5a5;
  background: #fff5f5;
}

.perm-action-chip--clear:hover:not(:disabled) {
  background: #fee2e2;
  border-color: #ef4444;
}

.perm-action-chip:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ── Lock Hint ── */
.perm-lock-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 20px 0 8px;
  font-size: 13px;
  color: var(--tx3, #94a3b8);
  font-weight: 500;
  justify-content: center;
}

/* ── Info Hint ── */
.perm-info-hint {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  margin-top: 10px;
  font-size: 11.5px;
  color: var(--tx3, #94a3b8);
  font-weight: 500;
  line-height: 1.5;
}

/* ── Pill Grid ── */
.perm-pill-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.perm-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  border: 1.5px solid var(--border, #d1d5db);
  background: var(--surface2, #f8fafc);
  color: var(--tx2, #475569);
  transition: all 0.18s ease;
  text-align: center;
  line-height: 1.3;
  white-space: nowrap;
}

.perm-pill:hover:not(.perm-pill--on) {
  border-color: var(--tx3, #94a3b8);
  background: var(--surface, #fff);
  transform: translateY(-1px);
}

.perm-pill--on {
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  transform: translateY(-1px);
}

/* ── Error ── */
.cm2-error {
  display: flex;
  align-items: center;
  font-size: 11.5px;
  color: #ef4444;
  font-weight: 600;
  margin-top: 8px;
}

.chip-premium {
  background: var(--brand, #0D9972) !important;
  color: #fff !important;
  font-weight: 700 !important;
  font-size: 11px !important;
}

@media (max-width: 480px) {
  .perm-stepper {
    padding: 10px 12px;
  }
}
.mem-type-badge {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  width: fit-content;
    white-space: nowrap; 
}

.type-owner {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
  border: 1px solid rgba(16, 185, 129, 0.2);
}
.type-tenant {
  background: rgba(245, 158, 11, 0.1);
  color: #d97706;
  border: 1px solid rgba(245, 158, 11, 0.2);
}
.type-default {
  background: rgba(100, 116, 139, 0.1);
  color: #64748b;
  border: 1px solid rgba(100, 116, 139, 0.2);
}

</style>
